import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  Bars3Icon,
  XMarkIcon,
  MagnifyingGlassIcon,
  ShoppingBagIcon,
  UserIcon,
  ChevronDownIcon,
} from "@heroicons/react/24/outline";

const cn = (...cls) => cls.filter(Boolean).join(" ");

const Navbar = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [pagesDropdownOpen, setPagesDropdownOpen] = useState(false);
  const [mobilePagesOpen, setMobilePagesOpen] = useState(false);

  const dropdownRef = useRef(null);

  // Close dropdown on outside click (desktop)
  useEffect(() => {
    const onClick = (e) => {
      if (!dropdownRef.current) return;
      if (!dropdownRef.current.contains(e.target)) setPagesDropdownOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  // Prevent body scroll when drawer open
  useEffect(() => {
    document.body.style.overflow = mobileOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [mobileOpen]);

  const closeMobile = () => {
    setMobileOpen(false);
    setMobilePagesOpen(false);
  };

  return (
    <>
      {/* ===== NAVBAR ===== */}
      <header className="sticky top-0 z-50 bg-black text-white border-b border-gray-800">
        <div className="max-w-7xl mx-auto px-4 lg:px-8 h-16 flex items-center justify-between relative">
          {/* LEFT - DESKTOP MENU */}
          <nav className="hidden lg:flex items-center gap-8 text-sm font-semibold">
            <Link to="/" className="hover:text-white/80 transition-colors">
              Accueil
            </Link>

            <Link to="/shop" className="hover:text-white/80 transition-colors">
              Produits
            </Link>

            {/* Pages Dropdown */}
            <div
              ref={dropdownRef}
              className="relative"
              onMouseEnter={() => setPagesDropdownOpen(true)}
              onMouseLeave={() => setPagesDropdownOpen(false)}
            >
              <button
                type="button"
                className="flex items-center gap-1 hover:text-white/80 transition-colors"
                onClick={() => setPagesDropdownOpen((v) => !v)}
              >
                Pages
                <ChevronDownIcon
                  className={cn(
                    "w-3 h-3 transition-transform duration-200",
                    pagesDropdownOpen && "rotate-180"
                  )}
                />
              </button>

              <div
                className={cn(
                  "absolute top-full left-0 mt-2 w-64 rounded-2xl bg-black border border-gray-800 shadow-xl overflow-hidden",
                  "transition-all duration-200",
                  pagesDropdownOpen ? "opacity-100 visible translate-y-0" : "opacity-0 invisible -translate-y-2"
                )}
              >
                <Link
                  to="/faq"
                  className="block px-4 py-3 text-sm hover:bg-gray-900 transition-colors border-b border-gray-800"
                  onClick={() => setPagesDropdownOpen(false)}
                >
                  FAQ
                </Link>

                <Link
                  to="/privacy"
                  className="block px-4 py-3 text-sm hover:bg-gray-900 transition-colors border-b border-gray-800"
                  onClick={() => setPagesDropdownOpen(false)}
                >
                  Politique de confidentialité
                </Link>

                <Link
                  to="/terms"
                  className="block px-4 py-3 text-sm hover:bg-gray-900 transition-colors"
                  onClick={() => setPagesDropdownOpen(false)}
                >
                  Conditions générales
                </Link>
              </div>
            </div>

            <Link to="/about" className="hover:text-white/80 transition-colors">
              À propos
            </Link>
          </nav>

          {/* MOBILE MENU ICON (LEFT) */}
          <button className="lg:hidden" onClick={() => setMobileOpen(true)} aria-label="Open menu">
            <Bars3Icon className="w-6 h-6" />
          </button>

          {/* CENTER LOGO */}
          <Link
            to="/"
            className="absolute left-1/2 transform -translate-x-1/2 text-xl font-extrabold tracking-wide uppercase"
          >
            ORIMA
          </Link>

          {/* RIGHT ICONS */}
          <div className="flex items-center gap-5">
            <button className="hidden sm:block" aria-label="Search">
              <MagnifyingGlassIcon className="w-5 h-5 cursor-pointer" />
            </button>

            <Link to="/cart" aria-label="Cart" className="hover:text-white/80 transition-colors">
              <ShoppingBagIcon className="w-5 h-5" />
            </Link>

            <button className="hidden sm:block" aria-label="User">
              <UserIcon className="w-5 h-5 cursor-pointer" />
            </button>
          </div>
        </div>
      </header>

      {/* ===== MOBILE DRAWER ===== */}
      {/* BACKDROP */}
      <div
        className={cn(
          "fixed inset-0 bg-black/50 z-40 transition-opacity duration-300",
          mobileOpen ? "opacity-100 visible" : "opacity-0 invisible"
        )}
        onClick={closeMobile}
      />

      {/* DRAWER */}
      <aside
        className={cn(
          "fixed top-0 left-0 h-full w-[290px] bg-black text-white z-50",
          "transform transition-transform duration-300 ease-out",
          mobileOpen ? "translate-x-0" : "-translate-x-full"
        )}
      >
        {/* HEADER */}
        <div className="h-16 px-4 border-b border-gray-800 flex items-center justify-between">
          <span className="text-lg font-extrabold tracking-wide uppercase">ORIMA</span>
          <button onClick={closeMobile} aria-label="Close menu">
            <XMarkIcon className="w-6 h-6" />
          </button>
        </div>

        {/* LINKS */}
        <div className="p-6 space-y-5">
          <Link to="/" onClick={closeMobile} className="block text-base font-semibold hover:text-white/80">
            Accueil
          </Link>

          <Link to="/shop" onClick={closeMobile} className="block text-base font-semibold hover:text-white/80">
            Produits
          </Link>

          {/* Pages Dropdown Mobile */}
          <div className="rounded-2xl border border-gray-800 overflow-hidden">
            <button
              onClick={() => setMobilePagesOpen((v) => !v)}
              className="w-full flex items-center justify-between px-4 py-3 text-base font-semibold hover:bg-gray-900"
              aria-expanded={mobilePagesOpen}
            >
              Pages
              <ChevronDownIcon className={cn("w-4 h-4 transition-transform duration-200", mobilePagesOpen && "rotate-180")} />
            </button>

            <div className={cn("transition-all duration-300", mobilePagesOpen ? "max-h-60 opacity-100" : "max-h-0 opacity-0")}>
              <div className="px-4 pb-3 pt-1 space-y-2">
                <Link to="/faq" onClick={closeMobile} className="block text-sm text-white/90 hover:text-white">
                  FAQ
                </Link>
                <Link to="/privacy" onClick={closeMobile} className="block text-sm text-white/90 hover:text-white">
                  Politique de confidentialité
                </Link>
                <Link to="/terms" onClick={closeMobile} className="block text-sm text-white/90 hover:text-white">
                  Conditions générales
                </Link>
              </div>
            </div>
          </div>

          <Link to="/about" onClick={closeMobile} className="block text-base font-semibold hover:text-white/80">
            À propos
          </Link>

          {/* Mobile quick icons */}
          <div className="pt-6 border-t border-gray-800 flex items-center gap-4">
            <button className="inline-flex items-center gap-2 text-sm font-semibold hover:text-white/80">
              <MagnifyingGlassIcon className="w-5 h-5" />
              Rechercher
            </button>

            <Link to="/cart" onClick={closeMobile} className="inline-flex items-center gap-2 text-sm font-semibold hover:text-white/80">
              <ShoppingBagIcon className="w-5 h-5" />
              Panier
            </Link>
          </div>
        </div>
      </aside>
    </>
  );
};

export default Navbar;
